import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Breadcrumbs,
  Link as MuiLink,
} from '@mui/material';
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { getReportDefinitionById } from '../../../api/reportsApi';
import { useI18n } from '../../../i18n';
import ExportMenu from './ExportMenu';
import {
  exportChartsPng,
  exportChartsPptx,
  exportCsv,
  exportElementPdf,
  exportXlsx,
} from '../export/exporters';
import { fileSlug } from '../export/tabular';

// SEAL's own navbar (Results menu, restrictions toggle) is chrome, not report.
const SEAL_PDF_IGNORE = 'nav.navbar, .navbar-fixed-top, .navbar-static-top';

const MIN_IFRAME_HEIGHT = 800;

const ReportViewContainer = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [searchParams] = useSearchParams();

  const periodFrom = searchParams.get('from') || '';
  const periodTo = searchParams.get('to') || '';

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(MIN_IFRAME_HEIGHT);
  const iframeRef = useRef(null);
  const resizeObserverRef = useRef(null);

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getReportDefinitionById(reportId);
      setReportData(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching report data:', err);
      setError(
        err?.status === 404 ? t('report.notFound') : t('report.loadError'),
      );
    } finally {
      setLoading(false);
    }
  }, [reportId, t]);

  useEffect(() => {
    if (reportId) fetchReportData();
  }, [reportId, fetchReportData]);

  useEffect(() => {
    setIframeLoaded(false);
  }, [reportData]);

  useEffect(() => () => resizeObserverRef.current?.disconnect(), []);

  // The SEAL page is served same-origin through the proxy, so its content
  // height is readable; growing the iframe to it avoids a nested scrollbar
  // that hides the lower charts on small screens.
  const handleIframeLoad = () => {
    setIframeLoaded(true);
    resizeObserverRef.current?.disconnect();
    setIframeHeight(MIN_IFRAME_HEIGHT);
    let doc;
    try {
      doc = iframeRef.current?.contentDocument;
    } catch {
      return;
    }
    if (!doc?.documentElement || typeof ResizeObserver === 'undefined') return;
    const fit = () =>
      setIframeHeight(
        Math.max(MIN_IFRAME_HEIGHT, doc.documentElement.scrollHeight),
      );
    resizeObserverRef.current = new ResizeObserver(fit);
    resizeObserverRef.current.observe(doc.body || doc.documentElement);
    fit();
  };

  // Exports read the iframe's live DOM, so they carry whatever restrictions
  // the user has applied inside SEAL since the initial render.
  const exportFormats = useMemo(() => {
    const doc = () => {
      const d = iframeRef.current?.contentDocument;
      if (!d?.body) throw new Error('Report document is not available');
      return d;
    };
    const name = reportData?.reportTitle || reportData?.code || `report_${reportId}`;
    const period = periodFrom || periodTo ? `_${periodFrom}_${periodTo}` : '';
    const baseName = `${fileSlug(name)}${period}`;
    return [
      {
        id: 'pdf',
        onExport: () =>
          exportElementPdf(doc().body, baseName, {
            headerLines: [name],
            ignoreSelector: SEAL_PDF_IGNORE,
          }),
      },
      { id: 'xlsx', onExport: () => exportXlsx(doc(), baseName) },
      { id: 'csv', onExport: () => exportCsv(doc(), baseName) },
      { id: 'png', onExport: () => exportChartsPng(doc(), baseName) },
      { id: 'pptx', onExport: () => exportChartsPptx(doc(), baseName, { title: name }) },
    ];
  }, [reportData, reportId, periodFrom, periodTo]);

  const handleBackToReports = () => {
    navigate('/dashboards');
  };

  // The iframe points at the dev-server proxy (src/setupProxy.js), which
  // does SWILogin + SWExecuteReport against Seal Report directly, runs
  // injectAndRewrite() on the returned HTML, and forwards Seal Report's
  // session cookies to the browser so subsequent iframe hops (through the
  // /api/seal-report/proxy/* catch-all) are authenticated.
  //
  // `from` / `to` are mapped by the proxy onto the report's own period
  // restriction, which SEAL renders prefilled — so no separate period
  // indicator is shown here.
  const reportIframeUrl = useMemo(() => {
    if (!reportId || !reportData?.path) return '';
    const params = new URLSearchParams({ path: reportData.path });
    if (periodFrom) params.set('from', periodFrom);
    if (periodTo) params.set('to', periodTo);
    return `/_seal/view/${reportId}?${params.toString()}`;
  }, [reportId, reportData, periodFrom, periodTo]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={handleBackToReports}>
          {t('report.back')}
        </Button>
      </Box>
    );
  }

  if (!reportData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('report.notFound')}
        </Alert>
        <Button variant="contained" onClick={handleBackToReports}>
          {t('report.back')}
        </Button>
      </Box>
    );
  }

  const title =
    reportData.reportTitle ||
    reportData.code ||
    t('report.fallbackName', { id: reportData.id });

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <MuiLink
          component={RouterLink}
          to="/dashboards"
          underline="hover"
          color="inherit"
        >
          {t('report.breadcrumb')}
        </MuiLink>
        <Typography color="text.primary">{title}</Typography>
      </Breadcrumbs>

      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h4">{title}</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <ExportMenu formats={exportFormats} disabled={!iframeLoaded} />
          <Button variant="outlined" onClick={handleBackToReports}>
            {t('report.back')}
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('report.viewer')}
        </Typography>
        <Box
          sx={{
            width: '100%',
            height: `${iframeHeight}px`,
            border: '1px solid #ccc',
            borderRadius: 1,
            position: 'relative',
          }}
        >
          {!iframeLoaded && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1,
                borderRadius: 1,
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  {t('report.loadingReport')}
                </Typography>
              </Box>
            </Box>
          )}
          <iframe
            ref={iframeRef}
            src={reportIframeUrl}
            width="100%"
            height="100%"
            style={{ border: 'none', borderRadius: '4px' }}
            title={t('reports.frameTitle', { title })}
            onLoad={handleIframeLoad}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default ReportViewContainer;
